import { Autocomplete, InputAdornment, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useRef, useState } from "react";

import useService from "../../../../../../hooks/useService";
import CustomArrowIcon from "../../../../../ui library/components/dropdowns/CustomArrowIcon";
import Svg from "../../../../../ui library/components/svgs/standard/svg";
import HNSColors from "../../../../../ui library/HNSColors";
import commonStyles from "../../../../../ui library/styles/commonStyles";
import CustomListBox from "../../../../Documents/UploadDocuments/components/CustomListBox";
import { applyConditionalRequiredRules, applyFieldTransformations } from "../../utils/functions";

import { HistoryTooltip } from "./HistoryTooltip";

const notRequiredOpt = { value: null, label: "No Selection" };

const DynamicField = ({
  conditionalRules = {},
  dynamicLayout,
  error,
  field,
  fieldTransformRules = {},
  formData,
  helperText,
  historyData = {},
  lostReason = null,
  options,
  setDynamicLayout,
  setErrorMessage,
  setErrors,
  setFormData,
  setOpenError,
  setOptions,
  setShowMoreButton,
  showMoreButton,
}) => {
  const enumRepository = useService("repositories/enum-repository");
  const [focusedInput, setFocusedInput] = useState(false);
  const [inputQuery, setInputQuery] = useState("");
  const [limit, setLimit] = useState(25);
  const [isLoading, setIsLoading] = useState(false);
  const searchTimersRef = useRef({});
  const loadedOnceRef = useRef(false);
  const { type, disabled, dependsOn, name, required, maxLength, label } = field || {};
  const value = formData[name];
  const isCurrencyField = type === "number";

  useEffect(() => {
    return () => {
      Object.values(searchTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  // Load options on mount if direct URL or has dependency with value
  useEffect(() => {
    if (type !== "select" || !name) {
      return;
    }

    const hasDirectURL = !!field?.optionsURL;
    const hasDependency = !!dependsOn && typeof field?.dynamicOptionsURL === "function";

    const depValue = hasDependency ? (formData?.[dependsOn] ?? null) : null;

    if (!hasDirectURL && !hasDependency) {
      return;
    }

    if (loadedOnceRef.current) {
      return;
    }

    if (hasDirectURL || (hasDependency && depValue !== null)) {
      loadFieldOptions({
        currentField: field,
        parentValue: depValue,
      });
      loadedOnceRef.current = true;
    }
  }, [type, name, dependsOn, formData?.[dependsOn]]);

  // Ensure current value is in options (in case it was set programmatically)
  useEffect(() => {
    if (type !== "select" || !name) {
      return;
    }
    if (value === null || value === undefined || value === "") {
      return;
    }

    const list = options?.[name] || [];
    const exists = list.some((o) => String(o?.value) === String(value));
    if (exists) {
      return;
    }

    const hasDependency = !!dependsOn && typeof field?.dynamicOptionsURL === "function";
    const depValue = hasDependency ? (formData?.[dependsOn] ?? null) : null;

    loadFieldOptions({
      currentField: field,
      parentValue: depValue,
      search: "",
      newLimit: 25,
      isFirstLoad: true,
    });
  }, [type, name, value]);

  async function loadFieldOptions({
    currentField,
    parentValue,
    search = "",
    newLimit = 25,
    isFirstLoad = false,
  }) {
    setIsLoading(true);
    setLimit(newLimit);
    const fieldName = currentField.name;

    let url =
      currentField.optionsURL ||
      (typeof currentField.dynamicOptionsURL === "function"
        ? currentField.dynamicOptionsURL(parentValue)
        : null);

    if (!url) {
      setIsLoading(false);
      return;
    }

    const connector = url.includes("?") ? "&" : "?";
    url += `${connector}limit=${newLimit}${search ? `&search=${encodeURIComponent(search)}` : ""}`;

    try {
      const results = await enumRepository.query({ urlPath: url });
      const hasNextPage = await enumRepository.getNextPage();
      const formattedOptions = results.map((opt, idx) => ({
        value: opt?.key ?? opt?.id ?? opt?.name,
        label: opt?.label ?? opt?.value ?? opt?.name ?? "",
        _uniqueId: `${opt?.label ?? opt?.name ?? "opt"}-${idx}`,
      }));

      const hasSelected =
        value !== null &&
        value !== undefined &&
        value !== "" &&
        formattedOptions.some((o) => String(o.value) === String(value));

      // If first load and current value not in options, and there's more to load, keep loading
      if (isFirstLoad && !hasSelected) {
        if (hasNextPage) {
          await loadFieldOptions({
            currentField,
            parentValue,
            search,
            newLimit: newLimit + 100,
            isFirstLoad,
          });
          return;
        }
      }

      setOptions((prev) => ({ ...prev, [fieldName]: formattedOptions }));
      setShowMoreButton((prev) => ({
        ...prev,
        [fieldName]: Boolean(hasNextPage),
      }));
    } catch (err) {
      setOpenError(true);
      setErrorMessage(err);
      setOptions((prev) => ({ ...prev, [fieldName]: [] }));
      setShowMoreButton((prev) => ({ ...prev, [fieldName]: false }));
    } finally {
      setIsLoading(false);
    }
  }

  function handleChange(fieldName, fieldValue) {
    setFormData((prev) => {
      const newData = { ...prev, [fieldName]: fieldValue };

      const layoutWithRequired = applyConditionalRequiredRules(
        fieldName,
        dynamicLayout,
        newData,
        conditionalRules
      );

      const layoutWithTransforms = applyFieldTransformations(
        fieldName,
        layoutWithRequired,
        newData,
        fieldTransformRules
      );

      setDynamicLayout(layoutWithTransforms);

      return newData;
    });

    setErrors((prev) => ({ ...prev, [fieldName]: "" }));

    // Reset dependent fields
    dynamicLayout.forEach(({ row }) => {
      row.forEach((rowField) => {
        if (rowField?.dependsOn === fieldName && rowField?.dynamicOptionsURL) {
          setFormData((prev) => ({ ...prev, [rowField.name]: "" }));
          setOptions((prev) => ({ ...prev, [rowField.name]: [] }));
          setShowMoreButton((prev) => ({ ...prev, [rowField.name]: false }));
        }
      });
    });
  }

  function handleShowMoreOptions(fieldName) {
    const selectedField = dynamicLayout.flatMap((r) => r.row).find((f) => f.name === fieldName);
    const parentValue = dependsOn ? formData[dependsOn] : "";

    if (selectedField) {
      const newLimit = limit + 25;

      loadFieldOptions({
        currentField: selectedField,
        parentValue,
        search: inputQuery || "",
        newLimit,
      });
    }
  }

  function handleInputFocus(newVal) {
    if (disabled) {
      setFocusedInput(false);
      return;
    }
    setFocusedInput(newVal);
  }

  function debouncedRemoteSearch(fieldName, term) {
    const selectedField = dynamicLayout.flatMap((r) => r.row).find((f) => f.name === fieldName);
    const parentValue = dependsOn ? formData[dependsOn] : "";
    if (!selectedField) {
      return;
    }

    if (searchTimersRef.current[fieldName]) {
      clearTimeout(searchTimersRef.current[fieldName]);
    }

    searchTimersRef.current[fieldName] = setTimeout(() => {
      loadFieldOptions({
        currentField: selectedField,
        parentValue,
        search: term || "",
      });
    }, 500);
  }

  const renderLabel = () => (
    <Typography
      sx={{
        ...commonStyles.label(focusedInput),
        fontSize: "14px",
      }}
    >
      {label}
      {required && <span className="font-bold">*</span>}
    </Typography>
  );

  if (type === "date") {
    return (
      <>
        {renderLabel()}
        <DatePicker
          name={name}
          disabled={disabled}
          readOnly={field?.readOnly}
          onChange={(newValue) => handleChange(name, newValue?.toISOString() ?? "")}
          value={value ? new Date(value) : null}
          slots={{
            openPickerIcon: () =>
              field?.readOnly ? null : (
                <Svg
                  color={disabled ? HNSColors.neutrals[500] : HNSColors.primary[500]}
                  name="his-calendar"
                  sx={{ margin: "0 1rem" }}
                />
              ),
          }}
          slotProps={{
            openPickerButton: {
              sx: { "&:hover": { background: "transparent" } },
            },
            textField: {
              disabled,
              fullWidth: true,
              sx: commonStyles.textField,
              size: "small",
              onBlur: () => handleInputFocus(false),
              onClick: () => handleInputFocus(true),
              error,
              helperText,
            },
          }}
          sx={{ padding: 0 }}
        />
      </>
    );
  }

  if (type === "select") {
    const blocked = field?.blockedValues ?? [];

    // IMPORTANT: value must be either the matched option object OR null (do NOT force notRequiredOpt)
    const selectedOption = (options[name] || []).find((opt) => opt.value === value) ?? null;

    return (
      <>
        <div className="flex justify-between">
          {renderLabel()}
          {field?.historyToolTip && !!historyData && (
            <HistoryTooltip
              historyData={historyData}
              lostReason={blocked.includes(value) ? lostReason : null}
            />
          )}
        </div>

        <Autocomplete
          name={name}
          disableClearable={!!required}
          disabled={disabled}
          sx={{ padding: 0 }}
          size="small"
          options={required ? options[name] || [] : [notRequiredOpt, ...(options[name] || [])]}
          getOptionLabel={(option) => (typeof option === "string" ? option : (option?.label ?? ""))}
          isOptionEqualToValue={(option, v) => !!v && option?.value === v?.value}
          value={selectedOption}
          inputValue={inputQuery || selectedOption?.label || ""}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input") {
              setInputQuery(newInputValue || "");
              debouncedRemoteSearch(name, newInputValue || "");
            }
          }}
          getOptionDisabled={(option) => blocked.includes(option?.value)}
          onChange={(_, newValue) => {
            if (newValue && blocked.includes(newValue?.value)) {
              return;
            }
            setInputQuery("");
            handleChange(name, newValue ? newValue.value : null);
          }}
          loading={isLoading}
          popupIcon={<CustomArrowIcon disabled={disabled} style={{ fontSize: "20px" }} />}
          ListboxComponent={
            showMoreButton[name]
              ? (props) => (
                  <CustomListBox
                    {...props}
                    showMore={true}
                    setShowMore={() => handleShowMoreOptions(name)}
                  />
                )
              : null
          }
          filterOptions={(opts) => opts} // Backend handles filtering
          renderInput={(params) => (
            <TextField
              {...params}
              error={error}
              fullWidth
              helperText={error ? helperText : ""}
              onBlur={() => handleInputFocus(false)}
              onClick={() => handleInputFocus(true)}
              placeholder="Select..."
              required={required}
              size="small"
              sx={commonStyles.textField}
              disabled={disabled}
            />
          )}
          renderOption={(props, option) => (
            <li {...props} key={option._uniqueId ?? option.value ?? option.label}>
              {option.label}
            </li>
          )}
        />
      </>
    );
  }

  return (
    <>
      {renderLabel()}
      <TextField
        error={error}
        fullWidth
        type={type}
        disabled={disabled}
        multiline={field?.rows > 1}
        inputProps={{ maxLength }}
        InputProps={{
          startAdornment: isCurrencyField ? (
            <InputAdornment
              sx={{
                paddingLeft: 1,
                "& .MuiTypography-root": { textAlign: "center" },
              }}
              position="start"
            >
              $
            </InputAdornment>
          ) : null,
          ...(isCurrencyField ? { step: "any" } : {}),
        }}
        minRows={field?.rows ?? 1}
        maxRows={field?.rows ?? 1}
        name={name}
        onBlur={() => handleInputFocus(false)}
        onChange={(e) => handleChange(name, e.target.value)}
        onClick={() => handleInputFocus(true)}
        required={required}
        size="small"
        sx={{
          ...commonStyles.textField,
          ...(isCurrencyField && {
            "& .MuiOutlinedInput-root .MuiInputBase-input": {
              paddingLeft: "0 !important",
            },
          }),
        }}
        value={value}
        helperText={error ? helperText : ""}
      />
    </>
  );
};

export default DynamicField;
